import {
  Box,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Tooltip,
  Typography,
} from "@mui/material"
import { useApi } from "../../hooks/useApi"
import { API_ENDPOINTS } from "../../api/endpoints"
import { BarChart } from "@mui/x-charts/BarChart"
import { PieChart } from "@mui/x-charts/PieChart"
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import dayjs, { Dayjs } from "dayjs"
import ExcelJS from "exceljs"
import "dayjs/locale/es"
import { useEffect, useState } from "react"
import { Button, Container, Table } from "react-bootstrap"
import { notifyInfo, notifySuccess } from "../../api/notify"
import { useSpinner } from "../../contexts/SpinnerProvider"
import {
  IEstadisticasPorFormaPago,
  IEstadisticasPorUsuario,
  IEstadisticasResponse,
  IReciboConEvento,
  IReporteTransferencia,
} from "../../interfaces/IEstadisticas"
import { FormaDePagoLabels, USUARIOS_CARGA } from "../../interfaces/IRecibo"
import { formatDateIsoWithTime } from "../../utils/dateUtils"
import { capitalizeNombresPropios } from "../../utils/StringUtils"
import { TipoEvento } from "../../interfaces/TipoEvento"
import { getDescripcion } from "../../utils/tipoEventoTheme"
import { obtenerAdicionalesRecibo } from "../../utils/reciboUtils"

dayjs.locale("es")

const Estadisticas = () => {
  const api = useApi()
  const { addLoading, removeLoading } = useSpinner()
  const API_URL = API_ENDPOINTS.ESTADISTICAS

  const [estadisticas, setEstadisticas] =
    useState<IEstadisticasResponse | null>(null)
  const [paginaActual, setPaginaActual] = useState(0)
  const [tipoFiltro, setTipoFiltro] = useState<"dia" | "rango" | "mes">(
    () => (localStorage.getItem("est_tipoFiltro") as "dia" | "rango" | "mes") || "dia",
  )
  const [fechaDesde, setFechaDesde] = useState<Dayjs | null>(dayjs())
  const [fechaHasta, setFechaHasta] = useState<Dayjs | null>(dayjs())
  const [mesSeleccionado, setMesSeleccionado] = useState<Dayjs | null>(null)
  const [ordenarPor, setOrdenarPor] = useState(
    () => localStorage.getItem("est_ordenarPor") || "",
  )
  const [direccion, setDireccion] = useState(
    () => localStorage.getItem("est_direccion") || "desc",
  )
  const [incluirEventosEliminados, setIncluirEventosEliminados] = useState(
    () => localStorage.getItem("est_incluirEliminados") === "true",
  )
  const [cargaInicial, setCargaInicial] = useState(true)
  const [periodoActual, setPeriodoActual] = useState("")
  const [tipoFiltroActual, setTipoFiltroActual] = useState<
    "dia" | "rango" | "mes"
  >("dia")
  const [filtroUsuarioCarga, setFiltroUsuarioCarga] = useState<string>(
    () => localStorage.getItem("est_filtroUsuario") || "",
  )
  const [tipoEvento, setTipoEvento] = useState<TipoEvento | "">(
    () => (localStorage.getItem("est_tipoEvento") as TipoEvento | "") || "",
  )

  useEffect(() => { localStorage.setItem("est_tipoFiltro", tipoFiltro) }, [tipoFiltro])
  useEffect(() => { localStorage.setItem("est_ordenarPor", ordenarPor) }, [ordenarPor])
  useEffect(() => { localStorage.setItem("est_direccion", direccion) }, [direccion])
  useEffect(() => { localStorage.setItem("est_incluirEliminados", String(incluirEventosEliminados)) }, [incluirEventosEliminados])
  useEffect(() => { localStorage.setItem("est_filtroUsuario", filtroUsuarioCarga) }, [filtroUsuarioCarga])
  useEffect(() => { localStorage.setItem("est_tipoEvento", tipoEvento) }, [tipoEvento])

  useEffect(() => {
    setCargaInicial(false)
  }, [])

  useEffect(() => {
    if (!cargaInicial && fechaDesde && fechaHasta) {
      buscarEstadisticas()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cargaInicial])

  const construirFechas = () => {
    let desde: string
    let hasta: string

    const offset = "-03:00"

    if (tipoFiltro === "dia") {
      const fecha =
        fechaDesde?.format("YYYY-MM-DD") || dayjs().format("YYYY-MM-DD")
      desde = `${fecha}T00:00:00${offset}`
      hasta = `${fecha}T23:59:59${offset}`
    } else if (tipoFiltro === "rango") {
      const fDesde =
        fechaDesde?.format("YYYY-MM-DD") || dayjs().format("YYYY-MM-DD")
      const fHasta =
        fechaHasta?.format("YYYY-MM-DD") || dayjs().format("YYYY-MM-DD")
      desde = `${fDesde}T00:00:00${offset}`
      hasta = `${fHasta}T23:59:59${offset}`
    } else {
      // mes
      const primerDia =
        mesSeleccionado?.startOf("month") || dayjs().startOf("month")
      const ultimoDia =
        mesSeleccionado?.endOf("month") || dayjs().endOf("month")

      desde = `${primerDia.format("YYYY-MM-DD")}T00:00:00${offset}`
      hasta = `${ultimoDia.format("YYYY-MM-DD")}T23:59:59${offset}`
    }

    return { desde, hasta }
  }

  const obtenerTextoPeriodo = () => {
    if (tipoFiltro === "dia") {
      return fechaDesde ? fechaDesde.format("DD/MM/YYYY") : ""
    } else if (tipoFiltro === "mes") {
      return mesSeleccionado ? mesSeleccionado.format("MMMM YYYY") : ""
    } else {
      const desde = fechaDesde ? fechaDesde.format("DD/MM/YYYY") : ""
      const hasta = fechaHasta ? fechaHasta.format("DD/MM/YYYY") : ""
      return `${desde} al ${hasta}`
    }
  }

  const obtenerTipoPeriodoTexto = () => {
    if (tipoFiltroActual === "dia") {
      return "día"
    } else if (tipoFiltroActual === "mes") {
      return "mes"
    } else {
      return "desde"
    }
  }

  const ordenarFormasDePago = (
    formasPago: IEstadisticasPorFormaPago[],
  ): IEstadisticasPorFormaPago[] => {
    const orden = ["TRANSFERENCIA", "EFECTIVO", "CREDITO", "DEBITO"]
    return [...formasPago].sort((a, b) => {
      const indexA = orden.indexOf(a.formaDePago)
      const indexB = orden.indexOf(b.formaDePago)
      return indexA - indexB
    })
  }

  const ordenarPorUsuario = (
    estadisticas: IEstadisticasPorUsuario[],
  ): IEstadisticasPorUsuario[] => {
    return [...estadisticas].sort((a, b) => {
      // "Sin asignar" siempre al final
      if (a.usuarioCarga === "Sin asignar") return 1
      if (b.usuarioCarga === "Sin asignar") return -1
      // Orden alfabético (A-Z)
      return a.usuarioCarga.localeCompare(b.usuarioCarga)
    })
  }

  const buscarEstadisticas = async (pagina: number = 0) => {
    addLoading()
    try {
      const { desde, hasta } = construirFechas()

      const params = new URLSearchParams({
        fechaDesde: desde,
        fechaHasta: hasta,
        pagina: pagina.toString(),
        tamanioPagina: "5",
        ordenarPor: ordenarPor || "fecha",
        direccion,
        incluirEventosEliminados: incluirEventosEliminados.toString(),
        ...(filtroUsuarioCarga && { usuarioCarga: filtroUsuarioCarga }),
        ...(tipoEvento && { tipoEvento }),
      })

      const response = await api.get<IEstadisticasResponse>(
        `${API_URL}?${params.toString()}`,
        { errorMessage: "Error al obtener estadisticas" },
      )

      setEstadisticas(response)
      setPaginaActual(pagina)
      setPeriodoActual(obtenerTextoPeriodo())
      setTipoFiltroActual(tipoFiltro)
    } catch {
      // toast ya mostrado por useApi
    } finally {
      removeLoading()
    }
  }

  const handleExportarReporteTransferencias = async () => {
    const fechas = construirFechas()
    const params = new URLSearchParams({
      fechaDesde: fechas.desde,
      fechaHasta: fechas.hasta,
    })

    addLoading()
    try {
      const transferencias = await api.get<IReporteTransferencia[]>(
        `${API_URL}/reporte-transferencias?${params.toString()}`,
        { errorMessage: "Error al obtener el reporte de transferencias" },
      )

      if (transferencias.length === 0) {
        notifyInfo("No hay transferencias de Usuario A para exportar", {
          autoClose: 3500,
        })
        return
      }

      const workbook = new ExcelJS.Workbook()
      const worksheet = workbook.addWorksheet("Transferencias")

      worksheet.columns = [
        { header: "Nombre", key: "nombre", width: 22 },
        { header: "Apellido", key: "apellido", width: 22 },
        { header: "DNI", key: "dni", width: 16 },
        { header: "Teléfono", key: "telefono", width: 18 },
        { header: "Entidad bancaria", key: "entidadBancaria", width: 26 },
        { header: "Monto", key: "monto", width: 16 },
        { header: "Fecha y hora", key: "fechaHora", width: 22 },
      ]

      worksheet.getRow(1).font = { bold: true }
      transferencias.forEach((transferencia) => {
        worksheet.addRow({
          nombre: capitalizeNombresPropios(transferencia.nombre),
          apellido: capitalizeNombresPropios(transferencia.apellido),
          dni: transferencia.dni,
          telefono: transferencia.telefono,
          entidadBancaria: transferencia.entidadBancaria,
          monto: transferencia.monto,
          fechaHora: formatDateIsoWithTime(transferencia.fechaHora),
        })
      })
      worksheet.getColumn("monto").numFmt = '"$"#,##0.00'

      const buffer = await workbook.xlsx.writeBuffer()
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      })
      const link = document.createElement("a")
      link.href = URL.createObjectURL(blob)
      link.download = `Reporte_transferencias_${dayjs().format("DD-MM-YYYY_HH-mm")}.xlsx`
      link.click()
      URL.revokeObjectURL(link.href)
      notifySuccess("Reporte de transferencias exportado", {
        autoClose: 3500,
      })
    } catch {
      // toast ya mostrado por useApi
    } finally {
      removeLoading()
    }
  }

  const renderAdicionales = (recibo: IReciboConEvento) => {
    const adicionales = obtenerAdicionalesRecibo(recibo)
    if (adicionales.length === 0) return "-"

    return (
      <div style={{ textAlign: "left" }}>
        {adicionales.map((adicional) => (
          <div key={adicional.label}>
            {adicional.label}: {adicional.cantidad}
          </div>
        ))}
      </div>
    )
  }

  const handleBuscar = () => {
    buscarEstadisticas(0)
  }

  const handleCambiarPagina = (nuevaPagina: number) => {
    buscarEstadisticas(nuevaPagina)
  }

  const handleOrdenar = (campo: string) => {
    if (ordenarPor === campo) {
      if (direccion === "asc") {
        setDireccion("desc")
      } else {
        setOrdenarPor("")
        setDireccion("desc")
      }
    } else {
      setOrdenarPor(campo)
      setDireccion("asc")
    }
  }

  useEffect(() => {
    if (estadisticas && !cargaInicial) {
      buscarEstadisticas(paginaActual)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordenarPor, direccion])

  const renderPaginacion = () => {
    if (!estadisticas || estadisticas.totalPaginas <= 1) return null

    return (
      <Box display="flex" justifyContent="center" mb={2}>
        <Pagination
          count={estadisticas.totalPaginas}
          page={paginaActual + 1}
          onChange={(event, value) => handleCambiarPagina(value - 1)}
          color="primary"
          showFirstButton
          showLastButton
          siblingCount={1}
          boundaryCount={1}
        />
      </Box>
    )
  }

  return (
    <Container fluid className="px-4">
      <h1 className="text-center mt-3 mb-4" style={{ fontSize: "2.25rem" }}>
        Estadísticas de Ventas
      </h1>

      {/* Filtros */}
      <Box mb={4}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Filtro</InputLabel>
                <Select
                  value={tipoFiltro}
                  label="Tipo de Filtro"
                  onChange={(e) =>
                    setTipoFiltro(e.target.value as "dia" | "rango" | "mes")
                  }
                >
                  <MenuItem value="dia">Día Específico</MenuItem>
                  <MenuItem value="mes">Mes Específico</MenuItem>
                  <MenuItem value="rango">Rango de Fechas</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {tipoFiltro === "dia" && (
              <Grid item xs={12} sm={6} md={2.5}>
                <DatePicker
                  label="Fecha"
                  value={fechaDesde}
                  onChange={(newValue) => setFechaDesde(newValue)}
                  format="DD/MM/YYYY"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                />
              </Grid>
            )}

            {tipoFiltro === "rango" && (
              <>
                <Grid item xs={12} sm={6} md={2.5}>
                  <DatePicker
                    label="Fecha Desde"
                    value={fechaDesde}
                    onChange={(newValue) => setFechaDesde(newValue)}
                    format="DD/MM/YYYY"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2.5}>
                  <DatePicker
                    label="Fecha Hasta"
                    value={fechaHasta}
                    onChange={(newValue) => setFechaHasta(newValue)}
                    format="DD/MM/YYYY"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                      },
                    }}
                  />
                </Grid>
              </>
            )}

            {tipoFiltro === "mes" && (
              <Grid item xs={12} sm={6} md={2.5}>
                <DatePicker
                  label="Mes"
                  value={mesSeleccionado}
                  onChange={(newValue) => setMesSeleccionado(newValue)}
                  views={["month", "year"]}
                  openTo="month"
                  format="MMMM YYYY"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6} md={1.65}>
              {" "}
              <FormControl fullWidth>
                {" "}
                <InputLabel>Usuario de Carga</InputLabel>{" "}
                <Select
                  value={filtroUsuarioCarga}
                  label="Usuario de Carga"
                  onChange={(e) => setFiltroUsuarioCarga(e.target.value)}
                >
                  {" "}
                  <MenuItem value="">Todos</MenuItem>{" "}
                  {USUARIOS_CARGA.map((usuario) => (
                    <MenuItem key={usuario} value={usuario}>
                      {" "}
                      {usuario}{" "}
                    </MenuItem>
                  ))}{" "}
                </Select>{" "}
              </FormControl>{" "}
            </Grid>
            <Grid item xs={12} sm={6} md={1.65}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Evento</InputLabel>
                <Select
                  value={tipoEvento ?? ""}
                  label="Tipo de Evento"
                  onChange={(e) => setTipoEvento(e.target.value as TipoEvento | "")}
                >
                  <MenuItem value=""><em>Todos</em></MenuItem>
                  {Object.values(TipoEvento).map((t) => (
                    <MenuItem key={t} value={t}>{getDescripcion(t)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md="auto">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={incluirEventosEliminados}
                    onChange={(e) =>
                      setIncluirEventosEliminados(e.target.checked)
                    }
                  />
                }
                label="Incluir recibos de eventos o egresados eliminados"
              />
            </Grid>

            <Grid item xs={12} sm={6} md="auto">
              <Button
                variant="primary"
                onClick={handleBuscar}
                style={{ height: "42px" }}
              >
                Buscar
              </Button>
            </Grid>

            <Grid item xs={12} sm={6} md="auto">
              <Button
                variant="success"
                onClick={handleExportarReporteTransferencias}
                style={{ height: "42px" }}
              >
                Exportar transferencias a Excel
              </Button>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Box>

      {/* Estadísticas Agregadas */}
      {estadisticas && (
        <>
          <Box mb={4}>
            <Typography variant="h5" gutterBottom>
              Datos {obtenerTipoPeriodoTexto()}{" "}
              {periodoActual && (
                <span style={{ color: "#606061ff", fontWeight: 600 }}>
                  {periodoActual}
                </span>
              )}
            </Typography>
          </Box>

          {/* Tarjetas de Métricas Principales */}
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} sm={6} md={1.8}>
              <Card elevation={3}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Recibos
                  </Typography>
                  <Typography variant="h4" component="div" color="primary">
                    {estadisticas.cantidadTotalRecibos}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={1.8}>
              <Card elevation={3}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Tarjetas Adulto
                  </Typography>
                  <Typography variant="h4" component="div" color="success.main">
                    {estadisticas.totalTarjetasAdulto}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={1.8}>
              <Card elevation={3}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Tarjetas Menor
                  </Typography>
                  <Typography variant="h4" component="div" color="info.main">
                    {estadisticas.totalTarjetasMenor}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={1.8}>
              <Card elevation={3}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Tarjetas Baile
                  </Typography>
                  <Typography variant="h4" component="div" color="secondary.main">
                    {estadisticas.totalTarjetasBaile}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={1.8}>
              <Card elevation={3}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Pagos de Seña
                  </Typography>
                  <Typography variant="h4" component="div" color="warning.main">
                    {estadisticas.cantidadSenias}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={12} md={3}>
              <Card elevation={3}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Monto Total
                  </Typography>
                  <Typography variant="h4" component="div" color="success.dark">
                    $
                    {estadisticas.montoTotalFinal.toLocaleString("de-DE", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Tabla de Detalles por Forma de Pago */}
          <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Desglose detallado por forma de pago
            </Typography>
            <Grid container spacing={2}>
              {ordenarFormasDePago(estadisticas.estadisticasPorFormaPago).map(
                (stat, idx) => (
                  <Grid item xs={12} sm={6} md={3} key={idx}>
                    <Box
                      sx={{
                        p: 2,
                        border: "1px solid #e0e0e0",
                        borderRadius: 1,
                        backgroundColor: "#f5f5f5",
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight="bold">
                        {FormaDePagoLabels[stat.formaDePago]}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Cantidad: {stat.cantidad} pago(s)
                      </Typography>
                      <Typography variant="h6" color="primary">
                        $
                        {stat.montoTotal.toLocaleString("de-DE", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Typography>
                    </Box>
                  </Grid>
                ),
              )}
            </Grid>
          </Paper>

          {/* Tabla de Detalles por Usuario de Carga */}
          <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Desglose detallado por usuario de carga
            </Typography>
            <Grid container spacing={2}>
              {estadisticas.estadisticasPorUsuario &&
                ordenarPorUsuario(estadisticas.estadisticasPorUsuario).map(
                  (stat, idx) => (
                    <Grid item xs={12} sm={6} md={3} key={idx}>
                      <Box
                        sx={{
                          p: 2,
                          border: "1px solid #e0e0e0",
                          borderRadius: 1,
                          backgroundColor: "#f5f5f5",
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight="bold">
                          {stat.usuarioCarga}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Cantidad: {stat.cantidad} recibo(s)
                        </Typography>
                        <Typography variant="h6" color="primary">
                          $
                          {stat.montoTotal.toLocaleString("de-DE", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </Typography>
                      </Box>
                    </Grid>
                  ),
                )}
              {(!estadisticas.estadisticasPorUsuario ||
                estadisticas.estadisticasPorUsuario.length === 0) && (
                <Grid item xs={12}>
                  <Typography
                    color="textSecondary"
                    sx={{ textAlign: "center" }}
                  >
                    No hay datos disponibles
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>

          {/* Gráficos */}
          <Grid container spacing={3} mb={4}>
            {/* Gráfico de Pastel - Distribución por Forma de Pago */}
            <Grid item xs={12} md={6}>
              <Paper elevation={3} sx={{ p: 1 }}>
                <Typography variant="h6" gutterBottom>
                  Distribución por Forma de Pago
                </Typography>
                {estadisticas.estadisticasPorFormaPago.length > 0 ? (
                  <PieChart
                    series={[
                      {
                        data: ordenarFormasDePago(
                          estadisticas.estadisticasPorFormaPago,
                        ).map((stat, idx) => ({
                          id: idx,
                          value: stat.montoTotal,
                          label: FormaDePagoLabels[stat.formaDePago],
                        })),
                        highlightScope: {
                          faded: "global",
                          highlighted: "item",
                        },
                        faded: {
                          innerRadius: 30,
                          additionalRadius: -30,
                          color: "gray",
                        },
                        arcLabel: (item) => {
                          const total =
                            estadisticas.estadisticasPorFormaPago.reduce(
                              (acc, stat) => acc + stat.montoTotal,
                              0,
                            )
                          const percentage = (
                            (item.value / total) *
                            100
                          ).toFixed(1)
                          return `${percentage}%`
                        },
                        arcLabelMinAngle: 35,
                      },
                    ]}
                    height={240}
                  />
                ) : (
                  <Typography
                    color="textSecondary"
                    sx={{ textAlign: "center", py: 5 }}
                  >
                    No hay datos disponibles
                  </Typography>
                )}
              </Paper>
            </Grid>

            {/* Gráfico de Barras - Montos por Forma de Pago */}
            <Grid item xs={12} md={6}>
              <Paper elevation={3} sx={{ p: 1 }}>
                <Typography variant="h6" gutterBottom>
                  Montos por Forma de Pago
                </Typography>
                {estadisticas.estadisticasPorFormaPago.length > 0 ? (
                  <BarChart
                    xAxis={[
                      {
                        scaleType: "band",
                        data: ordenarFormasDePago(
                          estadisticas.estadisticasPorFormaPago,
                        ).map((stat) => FormaDePagoLabels[stat.formaDePago]),
                      },
                    ]}
                    yAxis={[
                      {
                        valueFormatter: (value) => {
                          return `$${value.toLocaleString("de-DE", {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })}`
                        },
                      },
                    ]}
                    series={[
                      {
                        data: ordenarFormasDePago(
                          estadisticas.estadisticasPorFormaPago,
                        ).map((stat) => stat.montoTotal),
                        color: "#1976d2",
                      },
                    ]}
                    height={240}
                    margin={{ left: 85, right: 10, top: 10, bottom: 30 }}
                    tooltip={{ trigger: "none" }}
                  />
                ) : (
                  <Typography
                    color="textSecondary"
                    sx={{ textAlign: "center", py: 5 }}
                  >
                    No hay datos disponibles
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        </>
      )}

      {/* Tabla de Recibos */}
      {estadisticas && estadisticas.recibos.length > 0 && (
        <>
          <div className="table-responsive">
            <Table bordered hover>
              <thead>
                <tr>
                  <th className="text-center">Nº</th>
                  <th className="text-center">Número</th>
                  <th
                    className="text-center"
                    style={{ cursor: "pointer" }}
                    onClick={() => handleOrdenar("fecha")}
                  >
                    Fecha y hora
                    <span
                      style={{
                        fontSize: "0.9em",
                        display: "inline-block",
                        width: "1.5em",
                        textAlign: "center",
                      }}
                    >
                      {ordenarPor === "fecha" ? (
                        direccion === "asc" ? (
                          "▲"
                        ) : (
                          "▼"
                        )
                      ) : (
                        <span style={{ color: "#d3d3d3" }}>▲▼</span>
                      )}
                    </span>
                  </th>
                  <th
                    className="text-center"
                    style={{ cursor: "pointer" }}
                    onClick={() => handleOrdenar("usuarioCarga")}
                  >
                    Usuario de Carga
                    <span
                      style={{
                        fontSize: "0.9em",
                        display: "inline-block",
                        width: "1.5em",
                        textAlign: "center",
                      }}
                    >
                      {ordenarPor === "usuarioCarga" ? (
                        direccion === "asc" ? (
                          "▲"
                        ) : (
                          "▼"
                        )
                      ) : (
                        <span style={{ color: "#d3d3d3" }}>▲▼</span>
                      )}
                    </span>
                  </th>
                  <th className="text-center">Adulto</th>
                  <th className="text-center">Menor</th>
                  <th className="text-center">Baile</th>
                  <th className="text-center">Adicionales</th>
                  <th className="text-center">Forma de Pago</th>
                  <th className="text-center">Total</th>
                  <th
                    className="text-center"
                    style={{ cursor: "pointer" }}
                    onClick={() => handleOrdenar("egresado.evento.nombre")}
                  >
                    Nombre del Evento
                    <span
                      style={{
                        fontSize: "0.9em",
                        display: "inline-block",
                        width: "1.5em",
                        textAlign: "center",
                      }}
                    >
                      {ordenarPor === "egresado.evento.nombre" ? (
                        direccion === "asc" ? (
                          "▲"
                        ) : (
                          "▼"
                        )
                      ) : (
                        <span style={{ color: "#d3d3d3" }}>▲▼</span>
                      )}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {estadisticas.recibos.map((recibo, index) => (
                  <tr key={recibo.id}>
                    <td className="text-center">
                      {paginaActual * 5 + index + 1}
                    </td>
                    <td className="text-center">{recibo.numero}</td>
                    <td className="text-center">
                      {formatDateIsoWithTime(recibo.fecha)}
                    </td>
                    <td className="text-center">
                      {recibo.usuarioCarga || "-"}
                    </td>
                    <td className="text-center">{recibo.tarjetasAdulto || "-"}</td>
                    <td className="text-center">{recibo.tarjetasMenor || "-"}</td>
                    <td className="text-center">{recibo.tarjetasBaile || "-"}</td>
                    <td className="text-center">{renderAdicionales(recibo)}</td>
                    <td className="text-center">
                      {recibo.detallesPago && recibo.detallesPago.length > 0 ? (
                        recibo.detallesPago.length === 1 ? (
                          FormaDePagoLabels[recibo.detallesPago[0].formaDePago]
                        ) : (
                          <div style={{ textAlign: "left" }}>
                            {recibo.detallesPago.map((detalle, idx) => (
                              <div key={idx}>
                                {FormaDePagoLabels[detalle.formaDePago]}: $
                                {detalle.monto.toLocaleString("de-DE", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </div>
                            ))}
                          </div>
                        )
                      ) : (
                        "N/A"
                      )}
                    </td>
                    <td className="text-center">
                      $
                      {recibo.totalRecibo.toLocaleString("de-DE", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>
                    <Tooltip
                      title={
                        recibo.apellidoEgresado
                          ? `Egresado: ${capitalizeNombresPropios(recibo.apellidoEgresado)}, ${capitalizeNombresPropios(recibo.nombreEgresado ?? "")}`
                          : ""
                      }
                      placement="top"
                      arrow
                      slotProps={{
                        tooltip: {
                          sx: { fontSize: "0.80rem" },
                        },
                      }}
                    >
                      <td className="text-center">
                        {recibo.nombreEvento || "N/A"}
                      </td>
                    </Tooltip>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {renderPaginacion()}
        </>
      )}

      {estadisticas && estadisticas.recibos.length === 0 && (
        <div className="text-center py-5">
          <p className="text-muted">
            No se encontraron recibos para el rango de fechas seleccionado.
          </p>
        </div>
      )}
    </Container>
  )
}

export default Estadisticas
